import React, { useEffect, useRef } from 'react';
import type { ActiveGeoElement } from '../../../api';
import { ACCENT, tint } from '../../../theme';
import { CATEGORY, CATEGORY_DEFAULT, VERDICT } from '../../../strata';
import { LUX_SCALE_MAX } from '../lighting';
import '../styles/CanvasVisualizer.css';

// Canvas-scene constants -- the drawing's own rendering vocabulary,
// named once here, never scattered as literals mid-draw.
const SCENE = {
  ground: '#22314f',
  elementStroke: '#ffffff',
  popupBg: 'rgba(17,24,39,0.85)',
} as const;

interface CanvasVisualizerProps {
  elements: ActiveGeoElement[];
  // The illuminance heatmap: work-plane sample points (x, y, lux),
  // drawn as ground cells under the model. Sequential single-hue ramp
  // (amber, brightness = lux against the fixed LUX_SCALE_MAX reference).
  illuminancePoints?: [number, number, number][] | null;
  onElementMoved?: (originId: string, dx: number, dy: number) => void;
  onElementClick?: (element: ActiveGeoElement | null) => void;
  // Elements with open redline findings: rendered with a red halo.
  violatingIds?: Set<string>;
  // The LIGHTING redline as geography: cells below floorLux paint in
  // redline red when the heatmap is on, and each failing room's worst
  // point is pinned regardless -- both derived from the open
  // min-illuminance findings (the rule's own required/measured facts).
  luxRedline?: { floorLux: number; worstPoints: [number, number][] } | null;
  // The selected element: highlighted, and DRAGGABLE -- grabbing it
  // moves the ELEMENT (a plan-space edit op on drop), not the camera.
  selectedOriginId?: string | null;
  // Ghost, never vanish (APP_SPEC drilling): these elements render at
  // low opacity for context and are never pickable. The caller
  // decides membership (filter misses, peels); hidden mode simply
  // omits them from `elements` instead.
  ghostIds?: Set<string>;
  // Snap-to-interest: when this changes, the camera centers (and
  // zooms to a working scale) on the named element. The nonce lets
  // the same element be re-focused.
  focusTarget?: { originId: string; nonce: number } | null;
}

// A click that moved the mouse more than this (px) was a camera drag, not a
// pick attempt.
const CLICK_MOVE_THRESHOLD_PX = 5;
// Nearest pickable point must be within this many px of the click to count
// as a hit, rather than picking whatever's technically closest across the
// whole scene.
const PICK_RADIUS_PX = 12;
// Drag deltas snap to this world grid (meters): engineering moves are
// round numbers, and the drop posts exactly what the ghost showed.
const DRAG_SNAP_M = 0.005;

/**
 * Projects a 3D coordinate (x, y, z) into 2D canvas coordinates using azimuthal and elevation angles.
 */
function project3D(
  x: number, y: number, z: number,
  localScale: number,
  width: number, height: number,
  cx: number, cy: number, cz: number,
  angleTheta: number, anglePhi: number,
  panX: number, panY: number
): { x: number; y: number } {
  const dx = x - cx;
  const dy = y - cy;
  const dz = z - cz;

  const cosT = Math.cos(angleTheta);
  const sinT = Math.sin(angleTheta);
  const rx1 = dx * cosT - dy * sinT;
  const ry1 = dx * sinT + dy * cosT;
  const rz1 = dz;

  const cosP = Math.cos(anglePhi);
  const sinP = Math.sin(anglePhi);
  const rx = rx1;
  const ry = ry1 * cosP - rz1 * sinP;

  return {
    x: (width / 2) + rx * localScale + panX,
    y: (height / 2) - ry * localScale + panY
  };
}

export const CanvasVisualizer: React.FC<CanvasVisualizerProps> = ({
  elements,
  illuminancePoints,
  luxRedline,
  onElementClick,
  onElementMoved,
  violatingIds,
  selectedOriginId,
  ghostIds,
  focusTarget,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Camera State (stored in refs to avoid re-rendering React on every mouse move)
  const cameraRef = useRef({
    angleTheta: -60 * (Math.PI / 180),
    anglePhi: 25 * (Math.PI / 180),
    zoomScale: 1.0,
    panX: 0,
    panY: 0
  });

  // Interaction State
  const interactRef = useRef({
    isDragging: false,
    isPanning: false,
    lastMouseX: 0,
    lastMouseY: 0,
    lastTouchX: 0,
    lastTouchY: 0,
    lastTouchDist: 0,
    // Separate from lastMouseX/Y (which update every move) -- this stays
    // fixed at the mousedown position so mouseup can tell a real drag from
    // a click that jittered a couple pixels.
    downX: 0,
    downY: 0
  });

  // The last draw's projection basis (percentile center + base
  // scale) -- what a focus jump needs to aim the camera.
  const drawParamsRef = useRef<{ cx: number; cy: number; cz: number; baseScale: number } | null>(
    null,
  );

  // The widget drag: grabbing the SELECTED element moves the element,
  // not the camera. RAW world-space offset accumulates here; dx/dy
  // are the SNAPPED values (5 mm engineering grid -- precision is the
  // point of click-and-pull) that the ghost renders and the drop
  // posts. The ghost clears when server truth refetches.
  const elementDragRef = useRef<{
    originId: string;
    dx: number;
    dy: number;
    rawDx: number;
    rawDy: number;
  } | null>(null);
  // The last draw's projection scale -- needed to unproject screen
  // deltas into world meters while dragging.
  const projScaleRef = useRef(1);

  // Rebuilt every draw() call: one projected screen-space point per element,
  // for click hit-testing. A ref (not state) since it changes on every
  // camera move and must never trigger a re-render itself.
  const pickableRef = useRef<{ x: number; y: number; element: ActiveGeoElement }[]>([]);

  // Color follows the entity's category, from the strata paints the
  // legend and overview share -- the canvas is a PROJECTION of the
  // grammar, not an exception (strict-grammar ruling 2026-08-06).
  const getElementColor = (el: ActiveGeoElement): string => {
    if (el.compliance_failure) return VERDICT.violating;
    return (el.category && CATEGORY[el.category]) || CATEGORY_DEFAULT;
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width / window.devicePixelRatio;
    const height = canvas.height / window.devicePixelRatio;
    ctx.clearRect(0, 0, width, height);

    if (elements.length === 0) return;

    // One representative point per element (its first vertex is enough to
    // place it) drives the auto-fit -- a real project can contain a handful
    // of far-flung placeholder/context elements (e.g. a generic site-boundary
    // polygon shared across unrelated buildings on the same campus, seen in
    // real project data) that would otherwise stretch the naive min/max
    // bounding box across hundreds of meters and crush the actual building
    // into a tiny cluster of pixels. Percentiles ignore that handful of
    // outliers for the *fit*; every element still renders at its real
    // position below, just possibly off the initially-framed view.
    const xs: number[] = [];
    const ys: number[] = [];
    const zs: number[] = [];

    const firstCoord = (c: any): [number, number, number] | null => {
      if (!Array.isArray(c)) return null;
      if (typeof c[0] === 'number') return [c[0], c[1], c[2] ?? 0];
      return firstCoord(c[0]);
    };

    elements.forEach(el => {
      const geom = el.geometry;
      if (!geom || !geom.coordinates) return;
      const pt = firstCoord(geom.coordinates);
      if (pt) {
        xs.push(pt[0]);
        ys.push(pt[1]);
        zs.push(pt[2]);
      }
    });

    if (xs.length === 0) return;

    const percentile = (sorted: number[], p: number) => {
      const idx = (sorted.length - 1) * p;
      const lo = Math.floor(idx), hi = Math.ceil(idx);
      if (lo === hi) return sorted[lo];
      return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
    };

    const sortedXs = [...xs].sort((a, b) => a - b);
    const sortedYs = [...ys].sort((a, b) => a - b);
    const sortedZs = [...zs].sort((a, b) => a - b);

    const minX = percentile(sortedXs, 0.05), maxX = percentile(sortedXs, 0.95);
    const minY = percentile(sortedYs, 0.05), maxY = percentile(sortedYs, 0.95);
    const minZ = percentile(sortedZs, 0.05), maxZ = percentile(sortedZs, 0.95);

    const cx = minX + (maxX - minX) / 2;
    const cy = minY + (maxY - minY) / 2;
    const cz = minZ + (maxZ - minZ) / 2;

    const { angleTheta, anglePhi, zoomScale, panX, panY } = cameraRef.current;

    // Determine scale automatically to fit bounds
    const dx = maxX - cx;
    const dy = maxY - cy;
    // VERY rough heuristic for initial scale if bounds are large or small
    const maxRadius = Math.max(Math.sqrt(dx*dx + dy*dy), 0.1);
    const baseScale = Math.min(width, height) / (maxRadius * 2.5);
    const scale = baseScale * zoomScale;
    projScaleRef.current = scale;
    drawParamsRef.current = { cx, cy, cz, baseScale };

    const proj = (x: number, y: number, z: number) => project3D(
      x, y, z, scale, width, height, cx, cy, cz, angleTheta, anglePhi, panX, panY
    );

    // The 3D reference grid: a POUND-SIGN lattice, not a ground plane
    // or box faces -- lines run THROUGH the model volume along all three
    // axes at a fixed 5 m module (the model's units are meters; 5 m is
    // the drafting-conventional major for a building at this scale).
    const GRID_STEP = 5;
    const snapLo = (v: number) => Math.floor(v / GRID_STEP) * GRID_STEP;
    const snapHi = (v: number) => Math.ceil(v / GRID_STEP) * GRID_STEP;
    const bx0 = snapLo(minX), bx1 = snapHi(maxX);
    const by0 = snapLo(minY), by1 = snapHi(maxY);
    const bz0 = snapLo(minZ);
    ctx.lineWidth = 1;
    // The ground reference is a DOT grid on the floor plane -- one
    // dot per GRID_STEP meters at the model's z-floor. (The old
    // three-axis wireframe lattice read as walls of a box around the
    // building; the ground dots give the same spatial anchor without
    // the phantom planes.)
    ctx.fillStyle = SCENE.ground;
    const gridDotR = Math.max(scale * 0.015, 0.75);
    for (let gx = bx0; gx <= bx1; gx += GRID_STEP)
      for (let gy = by0; gy <= by1; gy += GRID_STEP) {
        const p = proj(gx, gy, bz0);
        ctx.beginPath();
        ctx.arc(p.x, p.y, gridDotR, 0, Math.PI * 2);
        ctx.fill();
      }

    // The illuminance heatmap: ground cells BEHIND the elements.
    // Sequential, one hue (dataviz law): lux ramps alpha+lightness on
    // amber against the ONE fixed reference (lighting.LUX_SCALE_MAX)
    // -- same color, same light level, in every project. Cells below
    // the open findings' floor flip to redline red: the advisory
    // becomes marked failing AREA, not just a dimmer amber.
    if (illuminancePoints && illuminancePoints.length > 0) {
      const cell = Math.max(scale * 0.5, 2); // 0.5 m grid cells
      const floor = luxRedline?.floorLux;
      for (const [px, py, lux] of illuminancePoints) {
        const p = proj(px, py, minZ);
        if (floor !== undefined && lux < floor) {
          const deficit01 = 1 - lux / floor;
          ctx.fillStyle = tint(VERDICT.violating, 0.12 + 0.45 * deficit01);
        } else {
          const t01 = Math.min(lux / LUX_SCALE_MAX, 1);
          ctx.fillStyle = tint(ACCENT.amber, 0.06 + 0.5 * t01);
        }
        ctx.fillRect(p.x - cell / 2, p.y - cell / 2, cell, cell);
      }
    }

    // Draw elements
    const pickable: { x: number; y: number; element: ActiveGeoElement }[] = [];

    const drag = elementDragRef.current;

    elements.forEach(el => {
      const geom = el.geometry;
      if (!geom) return;

      // Ghost, never vanish: excluded elements keep their shape at
      // low opacity for context, and are never pick targets.
      const ghost = ghostIds?.has(el.origin_id) ?? false;
      ctx.globalAlpha = ghost ? 0.12 : 1;

      const color = getElementColor(el);
      const violating = !ghost && (violatingIds?.has(el.origin_id) ?? false);
      const isSelected = el.origin_id === selectedOriginId;
      // The ghost: the in-flight drag offset applies to exactly the
      // dragged element until server truth replaces it.
      const ox = drag?.originId === el.origin_id ? drag.dx : 0;
      const oy = drag?.originId === el.origin_id ? drag.dy : 0;
      const projEl = (x: number, y: number, z: number) => proj(x + ox, y + oy, z);

      if (geom.type === 'Point') {
        const c = geom.coordinates;
        if (!c || c.length < 2) return;
        const pt = projEl(c[0], c[1], c[2] || 0);
        if (!ghost) pickable.push({ x: pt.x, y: pt.y, element: el });

        // Device dots scale WITH the building (px-per-meter), so
        // zooming out shrinks them into the fabric instead of leaving
        // a fixed-size dot soup; the glow ring and white outline are
        // zoom-earned detail, appearing only once dots are large
        // enough that they clarify rather than clutter.
        const dotR = Math.max(1.5, Math.min(10, scale * 0.18));

        if (violating) {
          // The redline halo.
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, dotR + 6, 0, Math.PI * 2);
          ctx.strokeStyle = VERDICT.violating;
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        if (dotR >= 5) {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, dotR * 2, 0, Math.PI * 2);
          ctx.fillStyle = color + '44'; // 25% opacity
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(pt.x, pt.y, dotR, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        if (isSelected || dotR >= 3) {
          ctx.strokeStyle = isSelected ? ACCENT.amber : SCENE.elementStroke;
          ctx.lineWidth = isSelected ? 2 : 1;
          ctx.stroke();
        }

      } else if (geom.type === 'Polygon' || geom.type === 'LineString') {
        // Simple polygon / linestring rendering
        const coords = geom.type === 'Polygon' ? geom.coordinates[0] : geom.coordinates;
        if (!coords || coords.length === 0) return;

        const path = () => {
          ctx.beginPath();
          const start = projEl(coords[0][0], coords[0][1], coords[0][2] || 0);
          ctx.moveTo(start.x, start.y);
          let sumX = start.x, sumY = start.y;
          for (let i = 1; i < coords.length; i++) {
            const pt = projEl(coords[i][0], coords[i][1], coords[i][2] || 0);
            ctx.lineTo(pt.x, pt.y);
            sumX += pt.x;
            sumY += pt.y;
          }
          return { cx: sumX / coords.length, cy: sumY / coords.length };
        };

        if (violating) {
          // The redline halo: a wide translucent red underlay stroke.
          path();
          if (geom.type === 'Polygon') ctx.closePath();
          ctx.strokeStyle = tint(VERDICT.violating, 0.45);
          ctx.lineWidth = 8;
          ctx.stroke();
        }

        const centroid = path();
        // Centroid of the projected points -- clicking anywhere near the
        // shape's visual center should hit it, not just its first vertex.
        if (!ghost) pickable.push({ x: centroid.cx, y: centroid.cy, element: el });

        if (geom.type === 'Polygon') {
          ctx.closePath();
          ctx.fillStyle = color + '1f'; // 12% opacity
          ctx.fill();
        }

        ctx.strokeStyle = isSelected ? ACCENT.amber : color;
        ctx.lineWidth = isSelected ? 3 : 2;
        ctx.stroke();
      }
    });

    ctx.globalAlpha = 1;
    pickableRef.current = pickable;

    // Worst-point pins: the exact spot each open lighting finding
    // names -- drawn OVER the elements so the redline's geography is
    // never buried, heatmap on or off.
    if (luxRedline) {
      ctx.strokeStyle = VERDICT.violating;
      ctx.lineWidth = 1.5;
      for (const [wx, wy] of luxRedline.worstPoints) {
        const p = proj(wx, wy, minZ);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x - 11, p.y);
        ctx.lineTo(p.x + 11, p.y);
        ctx.moveTo(p.x, p.y - 11);
        ctx.lineTo(p.x, p.y + 11);
        ctx.stroke();
      }
    }

    // The pull is QUANTIFIED while it happens: a top-center readout of
    // the snapped world delta (the exact numbers the drop will post).
    const liveDrag = elementDragRef.current;
    if (liveDrag && (liveDrag.dx !== 0 || liveDrag.dy !== 0)) {
      const sign = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(3)}`;
      const label = `Δx ${sign(liveDrag.dx)} m   Δy ${sign(liveDrag.dy)} m`;
      ctx.font = '12px monospace';
      const labelW = ctx.measureText(label).width;
      ctx.fillStyle = SCENE.popupBg;
      ctx.fillRect(width / 2 - labelW / 2 - 8, 10, labelW + 16, 22);
      ctx.fillStyle = ACCENT.amber;
      ctx.fillText(label, width / 2 - labelW / 2, 25);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      }
      draw();
    };

    window.addEventListener('resize', resize);
    resize();

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [elements]); // Re-bind resize if elements change drastically, but usually fine

  useEffect(() => {
    // Re-draw whenever elements update -- and clear any drag ghost:
    // server truth has arrived (or the project changed) and the offset
    // must never double-apply on top of refetched geometry.
    elementDragRef.current = null;
    draw();
  }, [elements]);

  useEffect(() => {
    // The heatmap layer arrives (or clears) without the elements
    // changing -- the canvas is imperative, so the prop must repaint
    // it explicitly. Kept separate from the elements effect: lux data
    // landing mid-drag must not clear the drag ghost.
    draw();
  }, [illuminancePoints]);

  // Snap-to-interest: aim the camera at the target's centroid. The
  // pan that centers a world point falls straight out of project3D
  // with pan=0 (panX = -rx*scale, panY = +ry*scale); zoom rises to a
  // working scale (~25 px/m) but never zooms OUT of a closer view.
  useEffect(() => {
    if (!focusTarget) return;
    const el = elements.find((e) => e.origin_id === focusTarget.originId);
    const dp = drawParamsRef.current;
    if (!el?.geometry || !dp) return;

    let sx = 0, sy = 0, sz = 0, n = 0;
    const walk = (node: unknown): void => {
      if (!Array.isArray(node)) return;
      if (typeof node[0] === 'number') {
        sx += node[0] as number;
        sy += (node[1] as number) ?? 0;
        sz += (node[2] as number) ?? 0;
        n += 1;
        return;
      }
      for (const child of node) walk(child);
    };
    walk(el.geometry.coordinates);
    if (n === 0) return;

    const cam = cameraRef.current;
    cam.zoomScale = Math.max(cam.zoomScale, Math.min(25 / dp.baseScale, 60));
    const scale = dp.baseScale * cam.zoomScale;
    const dx = sx / n - dp.cx;
    const dy = sy / n - dp.cy;
    const dz = sz / n - dp.cz;
    const cosT = Math.cos(cam.angleTheta);
    const sinT = Math.sin(cam.angleTheta);
    const rx = dx * cosT - dy * sinT;
    const ry1 = dx * sinT + dy * cosT;
    const ry = ry1 * Math.cos(cam.anglePhi) - dz * Math.sin(cam.anglePhi);
    cam.panX = -rx * scale;
    cam.panY = ry * scale;
    draw();
  }, [focusTarget]);

  // Screen-space delta -> world-space plan delta: the inverse of
  // project3D restricted to the horizontal plane (dz = 0). This is
  // what makes "drag the pipe half a meter" mean half a meter.
  const unprojectDelta = (screenDx: number, screenDy: number): { dx: number; dy: number } => {
    const { angleTheta, anglePhi } = cameraRef.current;
    const scale = projScaleRef.current || 1;
    const cosP = Math.cos(anglePhi);
    const rx1 = screenDx / scale;
    // Screen y grows downward; a near-horizontal camera (cosP -> 0)
    // cannot resolve plan-Y from screen-Y, so clamp the divisor.
    const ry1 = -screenDy / (scale * Math.max(Math.abs(cosP), 0.2) * Math.sign(cosP || 1));
    const cosT = Math.cos(angleTheta);
    const sinT = Math.sin(angleTheta);
    return { dx: rx1 * cosT + ry1 * sinT, dy: -rx1 * sinT + ry1 * cosT };
  };

  // Event Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    interactRef.current.isDragging = true;
    interactRef.current.isPanning = e.button === 2 || e.shiftKey;
    interactRef.current.lastMouseX = e.clientX;
    interactRef.current.lastMouseY = e.clientY;
    interactRef.current.downX = e.clientX;
    interactRef.current.downY = e.clientY;

    // Grabbing the SELECTED element starts a widget drag (the element
    // moves, not the camera). Anything else keeps camera semantics.
    if (selectedOriginId && onElementMoved && !interactRef.current.isPanning) {
      const hit = pickAt(e.clientX, e.clientY);
      if (hit?.origin_id === selectedOriginId) {
        elementDragRef.current = { originId: selectedOriginId, dx: 0, dy: 0, rawDx: 0, rawDy: 0 };
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const inter = interactRef.current;
    const cam = cameraRef.current;

    if (inter.isDragging) {
      const deltaX = e.clientX - inter.lastMouseX;
      const deltaY = e.clientY - inter.lastMouseY;

      const drag = elementDragRef.current;
      if (drag) {
        const world = unprojectDelta(deltaX, deltaY);
        drag.rawDx += world.dx;
        drag.rawDy += world.dy;
        drag.dx = Math.round(drag.rawDx / DRAG_SNAP_M) * DRAG_SNAP_M;
        drag.dy = Math.round(drag.rawDy / DRAG_SNAP_M) * DRAG_SNAP_M;
      } else if (inter.isPanning) {
        cam.panX += deltaX;
        cam.panY += deltaY;
      } else {
        cam.angleTheta += deltaX * 0.007;
        cam.anglePhi = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, cam.anglePhi + deltaY * 0.007));
      }

      inter.lastMouseX = e.clientX;
      inter.lastMouseY = e.clientY;
      draw();
    }
  };

  // Depth drilling (APP_SPEC): repeated clicks at the same spot cycle
  // down the stack of pickable elements under the cursor -- first the
  // nearest, then the one behind it, and around. No mode to enter;
  // drilling IS the click's second meaning.
  const pickCycleRef = useRef<{ x: number; y: number; cycle: number }>({
    x: -1,
    y: -1,
    cycle: 0,
  });

  const pickAt = (clientX: number, clientY: number): ActiveGeoElement | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const candidates = pickableRef.current
      .map((p) => ({ p, dist: Math.hypot(p.x - x, p.y - y) }))
      .filter((c) => c.dist < PICK_RADIUS_PX)
      .sort((a, b) => a.dist - b.dist);
    if (candidates.length === 0) {
      pickCycleRef.current = { x: -1, y: -1, cycle: 0 };
      return null;
    }
    const last = pickCycleRef.current;
    const samePlace = Math.hypot(last.x - x, last.y - y) < PICK_RADIUS_PX;
    const cycle = samePlace ? (last.cycle + 1) % candidates.length : 0;
    pickCycleRef.current = { x, y, cycle };
    return candidates[cycle].p.element;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    const inter = interactRef.current;
    const moved = Math.hypot(e.clientX - inter.downX, e.clientY - inter.downY);
    inter.isDragging = false;
    inter.isPanning = false;

    const drag = elementDragRef.current;
    if (drag && moved > CLICK_MOVE_THRESHOLD_PX) {
      // The drop: one edit op carries the whole accumulated move. The
      // ghost stays visible until server truth refetches.
      onElementMoved?.(drag.originId, drag.dx, drag.dy);
      return;
    }
    elementDragRef.current = null;

    if (moved <= CLICK_MOVE_THRESHOLD_PX) {
      onElementClick?.(pickAt(e.clientX, e.clientY));
    }
  };

  const handleMouseLeave = () => {
    interactRef.current.isDragging = false;
    interactRef.current.isPanning = false;
  };

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    const zoomIntensity = 0.08;
    const cam = cameraRef.current;
    if (e.deltaY < 0) {
      cam.zoomScale = Math.min(100.0, cam.zoomScale * (1.0 + zoomIntensity));
    } else {
      cam.zoomScale = Math.max(0.01, cam.zoomScale * (1.0 - zoomIntensity));
    }
    draw();
  };

  // React's synthetic onWheel attaches passively, so preventDefault() there
  // is ignored and the browser logs an error on every scroll. Zooming must
  // stop the page from scrolling, which requires a native non-passive
  // listener. Routed through a ref because handleWheel closes over draw(),
  // which is rebuilt each render.
  const wheelHandlerRef = useRef(handleWheel);
  wheelHandlerRef.current = handleWheel;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => wheelHandlerRef.current(e);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Keyboard camera (audit 2026-08-06: the drawing must be reachable
  // without a mouse). Same semantics as the mouse: arrows orbit,
  // shift+arrows pan, +/- zoom at the wheel's factor. The canvas is
  // role="application": it owns its keys while focused.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const cam = cameraRef.current;
    const orbitStep = 0.06;
    const panStep = 24;
    const zoomIntensity = 0.08;
    let handled = true;
    if (e.key === 'ArrowLeft') {
      if (e.shiftKey) cam.panX -= panStep;
      else cam.angleTheta -= orbitStep;
    } else if (e.key === 'ArrowRight') {
      if (e.shiftKey) cam.panX += panStep;
      else cam.angleTheta += orbitStep;
    } else if (e.key === 'ArrowUp') {
      if (e.shiftKey) cam.panY -= panStep;
      else cam.anglePhi = Math.min(Math.PI / 3, cam.anglePhi + orbitStep);
    } else if (e.key === 'ArrowDown') {
      if (e.shiftKey) cam.panY += panStep;
      else cam.anglePhi = Math.max(-Math.PI / 3, cam.anglePhi - orbitStep);
    } else if (e.key === '+' || e.key === '=') {
      cam.zoomScale = Math.min(100.0, cam.zoomScale * (1.0 + zoomIntensity));
    } else if (e.key === '-' || e.key === '_') {
      cam.zoomScale = Math.max(0.01, cam.zoomScale * (1.0 - zoomIntensity));
    } else {
      handled = false;
    }
    if (handled) {
      e.preventDefault();
      draw();
    }
  };

  // The drawing's accessible identity (audit 2026-08-06): what it
  // shows, said in words -- counts from the same props the pixels
  // come from, never a second source.
  const pinCount = luxRedline?.worstPoints.length ?? 0;
  const drawingLabel =
    `Project drawing: ${elements.length.toLocaleString()} elements` +
    (violatingIds && violatingIds.size > 0 ? `, ${violatingIds.size} with open redlines` : '') +
    (pinCount > 0 ? `, ${pinCount} low-light points pinned` : '') +
    '. Arrow keys orbit, shift plus arrows pan, plus and minus zoom.';

  return (
    <div className="canvas-visualizer-container" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="canvas-visualizer"
        role="application"
        aria-label={drawingLabel}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
};
